import { useMutation, useQuery, useQueryClient } from 'react-query';
import axios from 'axios';
import { predictionArbitrageManagerAxiosApi } from 'src/Config/axiosApi';

/* ------------------------------------------------------------------ */
/* Types — v4 (settlements + redeem, incremental)                      */
/* ------------------------------------------------------------------ */

export type Resolution = 'yes' | 'no' | 'unresolved';
export type MarketState =
  | 'complete'
  | 'pending_kalshi'
  | 'pending_redeem'
  | 'never_filled';
export type PendingReason = 'pending_kalshi' | 'pending_redeem';

export interface FinishMarket {
  pairId: string;
  pairName: string;
  ruleId: string | null;
  kalshiTicker: string | null;
  conditionId: string | null;
  state: MarketState;
  resolution: Resolution;
  kalshiNetUsd: number | null;
  polyNetUsd: number | null;
  pairNetUsd: number | null;
  polyRedeemUsd: number;
  winningContracts: number;
  redeemed: boolean;
}

export interface FinishExcludedEntry {
  pairId: string;
  pairName: string;
  ruleId: string | null;
  kalshiTicker: string | null;
  conditionId: string | null;
  state: 'excluded';
}

export interface FinishTotals {
  kalshiNetUsd: number;
  polymarketNetUsd: number;
  groupNetUsd: number;
}

export interface PnlSummary {
  kalshiNetUsd: number;
  polymarketNetUsd: number;
  groupNetUsd: number;
  computedCount: number;
  pendingCount: number;
  pendingByReason: { pending_kalshi: number; pending_redeem: number };
  excludedCount: number;
  lastRunAt: string;
  fullyFinished: boolean;
}

/**
 * Response of POST /groups/:groupId/finish — the result of an incremental run.
 */
export interface FinishSnapshot {
  groupId: string;
  snapshotId: string;
  computed: FinishMarket[];
  pending: FinishMarket[];
  excluded: FinishExcludedEntry[];
  totals: FinishTotals;
  summary: PnlSummary;
}

/* GET /groups/:groupId/finish — current state + breakdown + auto-suggestions */

export interface FinishLegDetail {
  side: 'yes' | 'no' | 'unconfirmed' | 'none';
  contracts: number;
  avgPrice: number | null;
  netUsd: number | null;
}

export interface FinishDetailPairEntry {
  pairId: string;
  pairName: string;
  ruleId: string | null;
  kalshiTicker: string | null;
  polyConditionId: string | null;
  resolution: Resolution;
  resolutionSource: string | null;
  state: MarketState | 'excluded';
  polyRedeemUsd: number;
  winningContracts: number;
  redeemed: boolean;
  kalshiLeg?: FinishLegDetail;
  polyLeg?: FinishLegDetail;
  pairNetUsd: number | null;
  flags: string[];
}

export interface GroupFinishState {
  groupId: string;
  groupName: string;
  status: 'partially_finished' | 'expired' | string;
  pnlSummary: PnlSummary | null;
  computed: FinishDetailPairEntry[];
  pending: FinishDetailPairEntry[];
  excluded: FinishDetailPairEntry[];
  autoSuggestExcludePairIds: string[];
}

export interface FinishResultSummary {
  groupId: string;
  groupName: string;
  status: 'partially_finished' | 'expired' | string;
  groupNetUsd: number;
  kalshiNetUsd: number;
  polymarketNetUsd: number;
  computedCount: number;
  pendingCount: number;
  pendingByReason: { pending_kalshi: number; pending_redeem: number };
  excludedCount: number;
  fullyFinished: boolean;
  lastRunAt: string | null;
}

export interface FinishResultsPage {
  items: FinishResultSummary[];
  total: number;
  page: number;
  limit: number;
}

/* ------------------------------------------------------------------ */
/* Query keys                                                          */
/* ------------------------------------------------------------------ */

export const groupFinishQueryKey = (groupId: string) => ['group-finish', groupId];
export const FINISH_RESULTS_KEY = ['finish-results'];

/* ------------------------------------------------------------------ */
/* Request functions (exported for wire-shape tests)                   */
/* ------------------------------------------------------------------ */

export interface FinishGroupBody {
  excludePairIds?: string[];
}

export async function finishGroup(
  groupId: string,
  body?: FinishGroupBody
): Promise<FinishSnapshot> {
  const { data } = await predictionArbitrageManagerAxiosApi.post<FinishSnapshot>(
    `/groups/${groupId}/finish`,
    body ?? {}
  );
  return data;
}

export async function fetchGroupFinishState(
  groupId: string
): Promise<GroupFinishState | null> {
  try {
    const { data } =
      await predictionArbitrageManagerAxiosApi.get<GroupFinishState>(
        `/groups/${groupId}/finish`
      );
    return data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 404) {
      return null;
    }
    throw err;
  }
}

export async function excludeGroupPairs(
  groupId: string,
  body: { pairIds: string[]; excluded: boolean }
): Promise<void> {
  await predictionArbitrageManagerAxiosApi.post(
    `/groups/${groupId}/exclude`,
    body
  );
}

export async function fetchFinishResultsList(
  page: number,
  limit: number
): Promise<FinishResultsPage> {
  const { data } =
    await predictionArbitrageManagerAxiosApi.get<FinishResultsPage>(
      '/finish/results',
      { params: { page, limit } }
    );
  return data;
}

export async function fetchFinishResultDetail(
  groupId: string
): Promise<GroupFinishState | null> {
  return fetchGroupFinishState(groupId);
}

/* ------------------------------------------------------------------ */
/* Mutations                                                           */
/* ------------------------------------------------------------------ */

/**
 * POST /groups/${groupId}/finish
 * Body: { excludePairIds? }
 * Incremental + re-runnable. On success: invalidate the group's finish state
 * + the results list.
 */
export function useFinishGroup(groupId: string) {
  const qc = useQueryClient();
  return useMutation(
    (body?: FinishGroupBody) => finishGroup(groupId, body),
    {
      mutationKey: ['entry:group-finish', groupId],
      onSuccess: () => {
        qc.invalidateQueries(groupFinishQueryKey(groupId));
        qc.invalidateQueries(FINISH_RESULTS_KEY);
      }
    }
  );
}

/**
 * POST /groups/${groupId}/exclude
 * Body: { pairIds, excluded } — set/clear excludedFromFinish on pairs.
 * On success: invalidate the group's finish state + the results list.
 */
export function useExcludeGroupPairs(groupId: string) {
  const qc = useQueryClient();
  return useMutation(
    (body: { pairIds: string[]; excluded: boolean }) =>
      excludeGroupPairs(groupId, body),
    {
      mutationKey: ['entry:group-exclude', groupId],
      onSuccess: () => {
        qc.invalidateQueries(groupFinishQueryKey(groupId));
        qc.invalidateQueries(FINISH_RESULTS_KEY);
      }
    }
  );
}

/* ------------------------------------------------------------------ */
/* Queries                                                             */
/* ------------------------------------------------------------------ */

/**
 * GET /groups/${groupId}/finish
 * Returns the current finish state for the group (summary + breakdown +
 * auto-suggested exclude list), or null if none exists (404).
 */
export function useGroupFinishState(groupId: string) {
  return useQuery<GroupFinishState | null>(
    groupFinishQueryKey(groupId),
    () => fetchGroupFinishState(groupId),
    {
      enabled: groupId.length > 0,
      staleTime: 30_000,
      keepPreviousData: true
    }
  );
}

/**
 * GET /finish/results?page=&limit=
 * Returns a paginated list of FinishResultSummary across all groups.
 */
export function useFinishResultsList(page: number, limit: number) {
  return useQuery<FinishResultsPage>(
    [...FINISH_RESULTS_KEY, page, limit],
    () => fetchFinishResultsList(page, limit),
    {
      keepPreviousData: true,
      staleTime: 60_000
    }
  );
}

/**
 * GET /groups/${groupId}/finish (detail drill-in)
 * Returns the current GroupFinishState for the group, or null on 404.
 */
export function useFinishResultDetail(groupId: string) {
  return useQuery<GroupFinishState | null>(
    ['finish-result-detail', groupId],
    () => fetchFinishResultDetail(groupId),
    {
      enabled: groupId.length > 0,
      staleTime: 30_000
    }
  );
}
